import React, { useContext } from 'react';
import moment from 'moment';
import { styled } from '@mui/material';

import { PageContext } from 'pages/PeerEvaluation/PageProvider';
import {
    Button,
    Icon,
    IconButton,
    Box,
    Dropdown,
    Stack,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Text,
    Divider,
} from 'shell/components';
import { useViewport } from 'contexts/viewport';

const getStatus = (peerEvaluation) => {
    let kind = 'fileUploaded';
    let text = 'Published';

    switch (peerEvaluation.status) {
        case 'draft': {
            kind = 'save';
            text = 'Saved as Draft';
            break;
        }
        case 'scheduled': {
            kind = 'calendar';
            text = `Scheduled for ${moment(peerEvaluation.schedule_on).format('MMM D[,] YYYY')}`;
            break;
        }
        case 'published':
        default:
            break;
    }

    return (
        <Stack alignItems={'center'} direction={'row'} spacing={3}>
            <Icon kind={kind} style={{ fontSize: 12 }} />
            <>{text}</>
        </Stack>
    );
};

const getDropDownOptions = (peerEvaluation) => {
    if (peerEvaluation.status === 'published') {
        return [
            { key: 'edit', value: 'Edit' },
            { key: 'unpublish', value: 'Unpublish' },
            { key: 'delete', value: 'Delete' },
        ];
    }
    return [
        { key: 'edit', value: 'Edit' },
        { key: 'delete', value: 'Delete' },
    ];
};

const PeerEvaluationTableInstructor = ({ peerEvaluationList = [], isInCourseDashboard }) => {
    const { device } = useViewport();
    const {
        members = [],
        handleOpenDialog,
        handleInstructorPages,

        //Constant
        DIALOG,
        INSTRUCTOR_PAGES,
    } = useContext(PageContext);

    const handleDropDown = (key, peerEvaluationId) => {
        switch (key) {
            case 'edit': {
                return handleInstructorPages(INSTRUCTOR_PAGES.EDIT, peerEvaluationId);
            }
            case 'unpublish': {
                return handleOpenDialog(DIALOG.PEER_EVALUATION_UNPUBLISH, { id: peerEvaluationId });
            }
            case 'delete': {
                return handleOpenDialog(DIALOG.PEER_EVALUATION_DELETE, { id: peerEvaluationId });
            }
            default:
                return;
        }
    };

    const renderMobile = () => {
        return (
            <Stack spacing={4}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'p2'} color={'light'}>
                        Name
                    </Text>
                    <IconButton
                        aria-label={'add a new peer evaluation'}
                        data-cy={'create-evaluation-button'}
                        kind={'add'}
                        size={'xsmall'}
                        variant={'circle'}
                        onClick={() => handleInstructorPages(INSTRUCTOR_PAGES.CREATE)}
                    />
                </Stack>
                {peerEvaluationList?.length > 0 && <Divider />}
                <Stack divider={<Divider />}>
                    {(peerEvaluationList || []).map((peerEvaluation) => (
                        <TabletPeerEvaluationStack key={peerEvaluation.id} spacing={1}>
                            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                <Text variant={'p1'}>{peerEvaluation.title}</Text>
                                <Dropdown
                                    id={`table-dropdown-${peerEvaluation.id}`}
                                    options={getDropDownOptions(peerEvaluation)}
                                    rootComponent={
                                        <IconButton
                                            aria-label={'more options'}
                                            data-cy={`table-dropdown-${peerEvaluation.title}`}
                                            kind={'moreHorizontal'}
                                            size={'medium'}
                                            variant={'square'}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    }
                                    onSelect={({ key }) => handleDropDown(key, peerEvaluation.id)}
                                />
                            </Stack>

                            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                {getStatus(peerEvaluation)}
                            </Stack>

                            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                <Text variant={'p2'} color={'light'}>
                                    {`Due: ${moment(peerEvaluation.due_on).format('MMM D')} / Available: ${moment(
                                        peerEvaluation.start_on
                                    ).format('MMM D')} - ${moment(peerEvaluation.end_on).format('MMM D')}`}
                                </Text>
                            </Stack>

                            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                <Text variant={'p2'} color={'light'}>
                                    {`Completion: ${peerEvaluation.submission_count || 0} / ${
                                        members.filter((member) => member.group_id).length
                                    }`}
                                </Text>
                            </Stack>
                            <Stack alignItems={'flex-start'}>
                                <CustomViewSubmissionButton
                                    data-cy={`view-submission-${peerEvaluation.title}`}
                                    variant={'tertiary'}
                                    onClick={() =>
                                        handleInstructorPages(INSTRUCTOR_PAGES.SUBMISSIONS, peerEvaluation.id)
                                    }
                                    sx={{ marginLeft: -2 }}
                                >
                                    View Submissions
                                </CustomViewSubmissionButton>
                            </Stack>
                        </TabletPeerEvaluationStack>
                    ))}
                </Stack>
                {peerEvaluationList?.length > 0 && <Divider />}
            </Stack>
        );
    };

    const renderTablet = () => {
        return (
            <Stack spacing={2}>
                <Stack>
                    <Text variant={'p2'} color={'light'}>
                        Name
                    </Text>
                </Stack>
                {peerEvaluationList?.length > 0 && <Divider />}
                <Stack divider={<Divider />}>
                    {(peerEvaluationList || []).map((peerEvaluation) => (
                        <TabletPeerEvaluationStack key={peerEvaluation.id} spacing={1}>
                            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                <Text variant={'p1'}>{peerEvaluation.title}</Text>
                                <Dropdown
                                    id={`table-dropdown-${peerEvaluation.id}`}
                                    options={getDropDownOptions(peerEvaluation)}
                                    rootComponent={
                                        <IconButton
                                            aria-label={'view submissions'}
                                            kind={'moreHorizontal'}
                                            size={'medium'}
                                            variant={'square'}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    }
                                    onSelect={({ key }) => handleDropDown(key, peerEvaluation.id)}
                                />
                            </Stack>

                            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                {getStatus(peerEvaluation)}
                            </Stack>

                            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                <Text variant={'p2'} color={'light'}>
                                    {`Due: ${moment(peerEvaluation.due_on).format('MMM D')} / Available: ${moment(
                                        peerEvaluation.start_on
                                    ).format('MMM D')} - ${moment(peerEvaluation.end_on).format('MMM D')}`}
                                </Text>
                            </Stack>

                            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                <Text variant={'p2'} color={'light'}>
                                    {`Completion: ${peerEvaluation.submission_count || 0} / ${
                                        members.filter((member) => member.group_id).length
                                    }`}
                                </Text>

                                <CustomViewSubmissionButton
                                    data-cy={`view-submission-${peerEvaluation.title}`}
                                    variant={'tertiary'}
                                    tabIndex={0}
                                    onClick={() =>
                                        handleInstructorPages(INSTRUCTOR_PAGES.SUBMISSIONS, peerEvaluation.id)
                                    }
                                >
                                    View Submissions
                                </CustomViewSubmissionButton>
                            </Stack>
                        </TabletPeerEvaluationStack>
                    ))}
                </Stack>
                {peerEvaluationList?.length > 0 && <Divider />}
            </Stack>
        );
    };

    const renderDesktop = () => {
        return (
            <TableOuterBox device={device} isInCourseDashboard={isInCourseDashboard}>
                <TableInnerBox device={device} isInCourseDashboard={isInCourseDashboard}>
                    <CustomTable device={device} size={'small'}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Due Date</TableCell>
                                <TableCell>Available from</TableCell>
                                <TableCell>Completion</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(peerEvaluationList || []).map((peerEvaluation) => (
                                <TableRow key={peerEvaluation.id}>
                                    <TableCell noWrap>
                                        <>{peerEvaluation.title}</>
                                    </TableCell>
                                    <TableCell>{getStatus(peerEvaluation)}</TableCell>
                                    <TableCell>{moment(peerEvaluation.due_on).format('MMM D [at] hh:mm a')}</TableCell>
                                    <TableCell>
                                        {moment(peerEvaluation.start_on).format('MMM D [at] hh:mm a')} -{' '}
                                        {moment(peerEvaluation.end_on).format('MMM D [at] hh:mm a')}
                                    </TableCell>
                                    <TableCell>
                                        <Stack alignItems={'center'} direction={'row'} spacing={8}>
                                            {peerEvaluation.submission_count || 0} /{' '}
                                            {members.filter((member) => member.group_id).length}
                                            <Button
                                                data-cy={`view-submission-${peerEvaluation.title}`}
                                                variant={'tertiary'}
                                                onClick={() =>
                                                    handleInstructorPages(
                                                        INSTRUCTOR_PAGES.SUBMISSIONS,
                                                        peerEvaluation.id
                                                    )
                                                }
                                            >
                                                View Submissions
                                            </Button>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Dropdown
                                            id={`table-dropdown-${peerEvaluation.id}`}
                                            options={getDropDownOptions(peerEvaluation)}
                                            rootComponent={
                                                <IconButton
                                                    aria-label={'more options'}
                                                    kind={'moreHorizontal'}
                                                    size={'medium'}
                                                    variant={'square'}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            }
                                            onSelect={({ key }) => handleDropDown(key, peerEvaluation.id)}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </CustomTable>
                </TableInnerBox>
            </TableOuterBox>
        );
    };

    const responsiveTable = {
        mobile: renderMobile(),
        tablet: renderTablet(),
        desktop: renderDesktop(),
    };

    return responsiveTable[device];
};

const TableOuterBox = styled(Box)(({ device, isInCourseDashboard, theme }) => {
    return {
        height: '100%',
        backgroundColor: theme.palette.ensightful.shades[0],
        borderRadius: '12px',
        ...(device === 'mobile' && {
            margin: theme.spacing(0, -5),
            width: '100vw',
            overflowX: 'auto',
        }),
        ...(device === 'tablet' && {
            margin: theme.spacing(0, -5),
            overflowX: 'auto',
            ...(isInCourseDashboard && {
                margin: theme.spacing(0, -4),
                width: 'calc(100vw - 229px - 40px - 1px)',
            }),
        }),
    };
});

const TableInnerBox = styled(Box)(({ device, isInCourseDashboard, theme }) => {
    return {
        ...(device === 'mobile' && {
            width: 'fit-content',
            padding: theme.spacing(0, 5),
        }),
        ...(device === 'tablet' && {
            width: 'fit-content',
            padding: theme.spacing(0, 5),
            ...(isInCourseDashboard && {
                padding: theme.spacing(0, 4),
            }),
        }),
    };
});

const CustomTable = styled(Table)(({ device, theme }) => {
    return {
        ...(device !== 'mobile' && {
            tableLayout: 'fixed',
            marginTop: theme.spacing(2),
            '& > thead > tr > th:nth-of-type(1)': {
                width: '20%',
            },
            '& > thead > tr > th:nth-of-type(2)': {
                width: '10%',
            },
            '& > thead > tr > th:nth-of-type(3)': {
                width: '15%',
            },
            '& > thead > tr > th:nth-of-type(4)': {
                width: '25%',
            },
            '& > thead > tr > th:nth-of-type(5)': {
                width: '20%',
            },
            '& > thead > tr > th:nth-of-type(6)': {
                width: '56px',
            },
            '& > tbody > tr > td:nth-of-type(1)': {
                width: '20%',
            },
            '& > tbody > tr > td:nth-of-type(2)': {
                width: '15%',
            },
            '& > tbody > tr > td:nth-of-type(3)': {
                width: '15%',
            },
            '& > tbody > tr > td:nth-of-type(4)': {
                width: '20%',
            },
            '& > tbody > tr > td:nth-of-type(5)': {
                width: '20%',
            },
            '& > tbody > tr > td:nth-of-type(6)': {
                width: '56px',
            },
        }),
        ...(device === 'tablet' && {
            minWidth: '50rem',
        }),
    };
});

const TabletPeerEvaluationStack = styled(Stack)(({ theme }) => {
    return {
        width: '100%',
        padding: theme.spacing(2, 4),
    };
});

const CustomViewSubmissionButton = styled(Button)(() => {
    return {};
});

export default PeerEvaluationTableInstructor;
